import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { PointSpending } from "../api/models"
import { addEntityFetchReducers, EntityState } from "./entity"
import { addPaginatedCollectionReducers, PaginatedCollectionState, usePaginatedCollection } from "./paginatedCollection"
import { logOut } from "./globalSlice"
import { RootState } from "./store"
import { useFetchableState } from "./fetchableState"
import Api from "../api/api"
import { claimRewardThunk } from "./storeSlice"

interface PointSpendingsState extends EntityState<PointSpending>, PaginatedCollectionState {
    debug: string
}

const spendingsInitialState: PointSpendingsState = {
    ids: undefined,
    link: null,
    currentRequestId: undefined,
    lastFetched: undefined,
    error: undefined,
    entities: {},
    debug: "point spendings state",
}


export const pointsSpendingsSlice = createSlice({
    name: 'pointSpendings',
    initialState: spendingsInitialState,
    reducers: {},
    extraReducers(builder) {
        addEntityFetchReducers<
            PointSpending,
            { pointSpending: PointSpending },
            { id: string, accessToken: string }
        >(
            builder,
            (payload) => payload.pointSpending,
            pointsSpendingThunk
        )
        addPaginatedCollectionReducers<
            PointSpending,
            { pointSpendings: PointSpending[], link: string | null },
            { reload: boolean, accessToken: string },
            PointSpendingsState
        >(
            builder,
            (payload) => payload.pointSpendings,
            pointsSpendingsThunk,
            spendingsReducerIntercept
        )

        builder.addCase(claimRewardThunk.fulfilled, (state, action) => {
            const pointSpending = action.payload.pointSpending
            if (!pointSpending)
                return
            state.entities[pointSpending.id] = {
                data: pointSpending,
                lastFetched: Date.now()
            }
            state.ids = [pointSpending.id, ...state?.ids ?? []]
        })

        builder.addCase(logOut, (state) => {
            state = spendingsInitialState;
        })
    },
})


export const pointsSpendingThunk = createAsyncThunk('points/fetchPointsSpending', async ({ id, accessToken }: { id: string, accessToken: string }, { getState, requestId }) => {
    const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
    const currentRequestId = state.pointSpendings.entities[id].currentRequestId
    if (currentRequestId !== requestId)
        throw new Error("Request outdated");
    return await Api.getPointSpending({ id, accessToken })
})

export const pointsSpendingsThunk = createAsyncThunk('points/fetchPointsSpendings', async ({ reload, accessToken }: { reload: boolean, accessToken: string }, { getState, requestId }) => {
    const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
    const currentRequestId = state.pointSpendings.currentRequestId
    if (currentRequestId !== requestId)
        throw new Error("Request outdated");
    return await Api.listPointSpendings({ link: state.topups.link, count: 10, accessToken })
})

const spendingsReducerIntercept = (type: any, state: PointSpendingsState, action: any) => {
    if (type === pointsSpendingsThunk.fulfilled) {
        const pointSpendings: PointSpending[] = action.payload.pointSpendings
        pointSpendings.forEach(ps => {
            const entity = state.entities[ps.id]
            if (entity?.currentRequestId !== undefined)
                return
            state.entities[ps.id] = {
                data: ps,
                lastFetched: Date.now()
            }
        })
    }
}

export const usePointSpending = (id: string, accessToken: string) => {
    return useFetchableState<PointSpending>({
        selector: (state: RootState) => state.pointSpendings.entities[id],
        action: pointsSpendingThunk({ id, accessToken })
    })
}

export const usePointSpendings = (accessToken: string) => {
    return usePaginatedCollection({
        selector: (state: RootState) => state.pointSpendings,
        moreAction: pointsSpendingsThunk({ reload: false, accessToken }),
        reloadAction: pointsSpendingsThunk({ reload: true, accessToken }),
    })
}

export default pointsSpendingsSlice.reducer